import React, { ReactNode } from 'react';
import { getImage } from 'gatsby-plugin-image';
import { Link } from 'gatsby';
import { StyledPostCard } from './styles';
import { Text } from '../Text/index';
import { Thumbnail } from '../Thumbnail';
import { MdContent } from '../MdContent';
import { ArticleMeta } from '../ArticleMeta';

export interface IPostCard {
  vertical?: boolean;
  id: string;
  timeToRead: number;
  tags: string[];
  date: string;
  title: string;
  thumbImage: any;
  excerpt: string;
  author: { name: string; image: any };
  slug: string;
  hero?: boolean;
  heading?: ReactNode;
}

export interface IPostCardVariation {
  vertical?: boolean;
  timeToRead: number;
  tags: string[];
  date: string;
  title: string;
  image: any;
  imageAlt: string;
  excerpt: string;
  author: { name: string; image: any };
  slug: string;
  // eslint-disable-next-line react/no-unused-prop-types
  heading?: ReactNode;
}

export const PostCardHero = ({
  vertical = false,
  timeToRead,
  tags,
  date,
  title: articleTitle,
  image,
  imageAlt,
  excerpt,
  author,
  slug: articleSlug,
  heading = `LATEST`,
}: IPostCardVariation) => (
  <div className="post-card post-card--hero">
    <div className="post-card__content">
      {typeof heading === `string` ? (
        <Text
          noRhythem
          type="title6"
          color="gray_8"
          className="post-card__caption"
        >
          {heading}
        </Text>
      ) : (
        heading
      )}

      <Link
        to={`/blog/${articleSlug}`}
        replace
        className="post-card__title-link"
      >
        <Text
          noRhythem
          type="title1"
          tag="h3"
          color="white_1"
          className="post-card__title post-card__title--top"
        >
          {articleTitle}
        </Text>
      </Link>
      <ArticleMeta
        author={author}
        tags={tags}
        timeToRead={timeToRead}
        date={date}
        center={!vertical}
        className="post-card__meta hero"
        imageSize="sm"
        disableAvatarLink
      />
    </div>
    <div className="post-card__h-divider" />
    <Link to={`/blog/${articleSlug}`} replace className="post-card__title-link">
      <Thumbnail imageSrc={image} alt={imageAlt} className="post-card__image" />
    </Link>
    <div className="post-card__h-divider" />
    <div className="post-card__content">
      <Link
        to={`/blog/${articleSlug}`}
        replace
        className="post-card__title-link"
      >
        <Text
          noRhythem
          className="post-card__body"
          type="body3"
          tag="div"
          color="gray_3"
        >
          <MdContent content={excerpt} />
        </Text>
      </Link>
    </div>
  </div>
);

export const PostCardNormal = ({
  vertical = false,
  timeToRead,
  tags,
  date,
  title: articleTitle,
  image,
  imageAlt,
  excerpt,
  author,
  slug: articleSlug,
}: IPostCardVariation) => (
  <div className="post-card post-card--normal">
    <Link
      to={`/blog/${articleSlug}`}
      replace
      className="post-card__thumbnail-link"
    >
      <Thumbnail imageSrc={image} alt={imageAlt} className="post-card__image" />
    </Link>
    <div className="post-card__h-divider" />
    <div className="post-card__content">
      <ArticleMeta
        author={author}
        tags={tags}
        timeToRead={timeToRead}
        date={date}
        center={!vertical}
        className="post-card__meta normal"
        disableAvatarLink
      />
      <Link to={`/blog/${articleSlug}`} replace>
        <Text
          noRhythem
          type="title6"
          tag="h6"
          color="white_1_75"
          className="post-card__title"
        >
          {articleTitle}
        </Text>
        <Text
          noRhythem
          className="post-card__body"
          type="body3"
          tag="div"
          color="gray_3"
        >
          <MdContent content={excerpt} />
        </Text>
      </Link>
    </div>
  </div>
);

export const PostCard = ({
  vertical = false,
  id,
  timeToRead,
  tags,
  date,
  title,
  thumbImage,
  excerpt,
  author,
  slug,
  hero = false,
  heading,
}: IPostCard) => {
  const { description: imageAlt } = thumbImage;
  const image = getImage(thumbImage);
  const variantProps = {
    vertical,
    timeToRead,
    tags,
    date,
    title,
    excerpt,
    author,
    slug,
    image,
    imageAlt,
    heading,
  };

  return (
    <StyledPostCard key={id} vertical={vertical} hero={hero}>
      {hero ? (
        <PostCardHero {...variantProps} />
      ) : (
        <PostCardNormal {...variantProps} />
      )}
    </StyledPostCard>
  );
};
